import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Plus, Archive, RotateCcw, Loader2, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  JOB_STATUS_FLOW,
  JOB_STATUS_LABELS,
  PRIORITY_LABELS,
  jobStatusTone,
} from "@/lib/constants";
import { formatCurrency, formatDate } from "@/lib/format";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function JobsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const canCreate = !isMobile && hasRole(user, "admin", "sales", "order_manager");
  const isAdmin = hasRole(user, "admin");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Form input states
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [salespersonId, setSalespersonId] = useState(searchParams.get("salesperson_id") || "");
  const [productCategoryId, setProductCategoryId] = useState(searchParams.get("product_category_id") || "");
  const [district, setDistrict] = useState(searchParams.get("district") || "");
  const [includeArchived, setIncludeArchived] = useState(searchParams.get("include_archived") === "true");

  const [productCategories, setProductCategories] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Sync inputs with URL searchParams when searchParams change (e.g. Browser Back)
  useEffect(() => {
    setQ(searchParams.get("q") || "");
    setStatus(searchParams.get("status") || "");
    setSalespersonId(searchParams.get("salesperson_id") || "");
    setProductCategoryId(searchParams.get("product_category_id") || "");
    setDistrict(searchParams.get("district") || "");
    setIncludeArchived(searchParams.get("include_archived") === "true");
  }, [searchParams]);

  const activeFilterCount = [
    searchParams.get("q"),
    searchParams.get("status"),
    searchParams.get("product_category_id"),
    searchParams.get("salesperson_id"),
    searchParams.get("district"),
    searchParams.get("include_archived") === "true" ? "archived" : "",
    searchParams.get("only_delayed_installations") === "true" ? "delayed" : "",
    searchParams.get("contract_date_from") ? "contract_date_from" : "",
    searchParams.get("contract_date_to") ? "contract_date_to" : "",
    searchParams.get("closing_date_from") ? "closing_date_from" : "",
    searchParams.get("closing_date_to") ? "closing_date_to" : "",
    searchParams.get("exclude_teklif") === "true" ? "exclude_teklif" : "",
  ].filter(Boolean).length;

  const applyFiltersToUrl = (overrides = {}) => {
    const nextQ = overrides.q !== undefined ? overrides.q : q;
    const nextStatus = overrides.status !== undefined ? overrides.status : status;
    const nextSalespersonId = overrides.salespersonId !== undefined ? overrides.salespersonId : salespersonId;
    const nextProductCategoryId = overrides.productCategoryId !== undefined ? overrides.productCategoryId : productCategoryId;
    const nextDistrict = overrides.district !== undefined ? overrides.district : district;
    const nextIncludeArchived = overrides.includeArchived !== undefined ? overrides.includeArchived : includeArchived;
    const nextPage = overrides.page !== undefined ? overrides.page : 1;

    const p = new URLSearchParams();

    // Preserve existing dashboard filters if present unless cleared
    const preservedKeys = [
      "only_delayed_installations",
      "contract_date_from",
      "contract_date_to",
      "closing_date_from",
      "closing_date_to",
      "exclude_teklif",
    ];
    for (const key of preservedKeys) {
      if (searchParams.has(key)) p.set(key, searchParams.get(key));
    }

    if (nextQ) p.set("q", nextQ);
    if (nextStatus) {
      p.set("status", nextStatus);
      p.delete("exclude_teklif");
    }
    if (nextSalespersonId) p.set("salesperson_id", nextSalespersonId);
    if (nextProductCategoryId) p.set("product_category_id", nextProductCategoryId);
    if (nextDistrict) p.set("district", nextDistrict);
    if (nextIncludeArchived) p.set("include_archived", "true");
    if (nextPage > 1) p.set("page", String(nextPage));

    setSearchParams(p);
  };

  const clearFilters = () => {
    setQ("");
    setStatus("");
    setSalespersonId("");
    setProductCategoryId("");
    setDistrict("");
    setIncludeArchived(false);
    setSearchParams({});
  };

  const loadReferences = async () => {
    try {
      const [pcs, sps] = await Promise.all([
        api.get("/product-categories"),
        isAdmin ? api.get("/users/salespeople") : Promise.resolve({ data: [] }),
      ]);
      setProductCategories(pcs.data);
      setSalespeople(sps.data);
    } catch (err) {
      // silent
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      for (const [key, val] of searchParams.entries()) {
        params[key] = val;
      }
      if (!params.page) params.page = 1;
      if (!params.page_size) params.page_size = 25;

      const r = await api.get("/jobs", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch whenever URL searchParams change
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    applyFiltersToUrl({ page: 1 });
  };

  const handleArchive = async (job) => {
    if (isMobile) return;
    try {
      await api.post(`/jobs/${job.id}/archive`);
      toast.success("İş arşivlendi.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleUnarchive = async (job) => {
    if (isMobile) return;
    try {
      await api.post(`/jobs/${job.id}/unarchive`);
      toast.success("İş arşivden çıkarıldı.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-[#F4F6F8] min-h-full" data-testid="jobs-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#171A1F] tracking-tight">İşler</h1>
          <p className="text-xs text-slate-500 mt-1">Tüm satış ve montaj süreçlerinin merkezi listesi.</p>
        </div>
        {canCreate && (
          <Button
            onClick={() => navigate("/isler/yeni")}
            className="bg-[#FF353A] hover:bg-[#E02B30] text-white shadow-sm"
            data-testid="jobs-create-button"
          >
            <Plus size={16} className="mr-2" /> Yeni İş Oluştur
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-[#DCE2E8] bg-white p-4 shadow-sm">
        {/* Search bar & mobile filter trigger button */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="İş no, müşteri, telefon ara..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 border-[#DCE2E8] focus-visible:ring-[#FF353A] text-base md:text-sm h-11 md:h-9"
              data-testid="jobs-search-input"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => setMobileFilterOpen((v) => !v)}
            className="md:hidden border-[#DCE2E8] min-h-[44px] min-w-[44px] px-3 relative flex items-center gap-2"
            data-testid="jobs-mobile-filter-trigger"
          >
            <Filter size={18} />
            <span className="text-xs font-medium">Filtreler</span>
            {activeFilterCount > 0 && (
              <span className="h-5 w-5 rounded-full bg-[#FF353A] text-white text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>

        {/* Filters Container (Desktop always visible, Mobile toggleable) */}
        <form
          onSubmit={handleSearch}
          className={`mt-3 grid gap-3 md:grid-cols-6 ${mobileFilterOpen ? "block" : "hidden md:grid"}`}
        >
          <Select value={status || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setStatus(next); applyFiltersToUrl({ status: next, page: 1 }); }}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="jobs-status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__all__">Tüm Durumlar</SelectItem>
              {JOB_STATUS_FLOW.map(([k, l]) => (
                <SelectItem key={k} value={k}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={productCategoryId || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setProductCategoryId(next); applyFiltersToUrl({ productCategoryId: next, page: 1 }); }}>
            <SelectTrigger className="border-[#DCE2E8]" data-testid="jobs-product-filter"><SelectValue placeholder="Ürün Grubu" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DCE2E8]">
              <SelectItem value="__all__">Tüm Ürünler</SelectItem>
              {productCategories.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {isAdmin && (
            <Select value={salespersonId || "__all__"} onValueChange={(v) => { const next = v === "__all__" ? "" : v; setSalespersonId(next); applyFiltersToUrl({ salespersonId: next, page: 1 }); }}>
              <SelectTrigger className="border-[#DCE2E8]" data-testid="jobs-salesperson-filter"><SelectValue placeholder="Satış Personeli" /></SelectTrigger>
              <SelectContent className="bg-white border-[#DCE2E8]">
                <SelectItem value="__all__">Tüm Personel</SelectItem>
                {salespeople.map((sp) => (
                  <SelectItem key={sp.id} value={sp.id}>{sp.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Input
            placeholder="İlçe"
            value={district}
            onChange={(e) => setDistrict(e.target.value)}
            className="border-[#DCE2E8] focus-visible:ring-[#FF353A] text-base md:text-sm h-11 md:h-9"
            data-testid="jobs-district-filter"
          />

          <div className="flex flex-wrap items-center gap-2 md:col-span-6 pt-1 border-t border-slate-100 md:border-t-0">
            <Button
              type="submit"
              variant="secondary"
              className="bg-[#F4F6F8] hover:bg-slate-200 text-[#171A1F] border border-[#DCE2E8] min-h-[44px] md:min-h-0 min-w-[80px]"
              data-testid="jobs-filter-apply"
            >
              Uygula
            </Button>
            {activeFilterCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-500 hover:text-slate-800 text-xs min-h-[44px] md:min-h-0"
                data-testid="jobs-filter-clear"
              >
                <X size={14} className="mr-1" /> Temizle
              </Button>
            )}
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer py-2">
                <input
                  type="checkbox"
                  checked={includeArchived}
                  onChange={(e) => { setIncludeArchived(e.target.checked); applyFiltersToUrl({ includeArchived: e.target.checked, page: 1 }); }}
                  data-testid="jobs-include-archived"
                  className="h-4 w-4 rounded border-slate-300 text-[#FF353A] focus:ring-[#FF353A]"
                />
                Arşivlenenleri göster
              </label>
            )}
            <span className="ml-auto text-xs text-slate-500 py-1">Toplam <span className="font-semibold text-[#171A1F]">{total.toLocaleString("tr-TR")}</span> kayıt</span>
          </div>
        </form>
      </div>

      <div className="rounded-lg border border-[#DCE2E8] bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500" data-testid="jobs-loading">
            <Loader2 className="animate-spin text-[#FF353A]" size={16} /> Yükleniyor...
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            testId="jobs-empty"
            title="Kayıt bulunamadı"
            description="Filtreleri temizleyin veya yeni bir iş oluşturun."
          />
        ) : (
          <>
            {/* Mobile Card List View */}
            <div className="block md:hidden divide-y divide-[#DCE2E8]">
              {items.map((j) => (
                <div
                  key={j.id}
                  onClick={() => navigate(`/isler/${j.id}`)}
                  className="p-4 space-y-2 cursor-pointer hover:bg-[#EAF7FF]/20 active:bg-[#EAF7FF]/50 transition-colors"
                  data-testid={`job-card-${j.job_number}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-mono text-xs font-bold text-[#FF353A]">{j.job_number}</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge tone={jobStatusTone(j.status)} testId={`job-status-mobile-${j.job_number}`}>
                        {JOB_STATUS_LABELS[j.status] || j.status}
                      </StatusBadge>
                      {j.is_installation_delayed && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-800 border border-rose-200">
                          Montaj Gecikmiş
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#171A1F]">{j.customer_name}</div>
                    {j.company_name && <div className="text-xs text-slate-500">{j.company_name}</div>}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 pt-1 border-t border-slate-100">
                    <div>
                      <span className="text-slate-400 block text-[10px]">İlçe / Ürün</span>
                      <span className="truncate font-medium block">{[j.district, j.product_group_names?.join(", ")].filter(Boolean).join(" · ") || "-"}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Satış Tutarı</span>
                      <span className="font-mono font-semibold text-[#171A1F]">{formatCurrency(j.sales_amount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Sözleşme Tarihi</span>
                      <span>{formatDate(j.contract_date)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block text-[10px]">Montaj Tarihi</span>
                      <span>{formatDate(j.planned_installation_start)}</span>
                    </div>
                  </div>
                  {isAdmin && !isMobile && (
                    <div className="pt-2 flex justify-end">
                      {j.is_archived ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleUnarchive(j); }}
                          className="text-xs h-8 border-[#DCE2E8]"
                          data-testid={`job-mobile-unarchive-${j.job_number}`}
                        >
                          <RotateCcw size={12} className="mr-1" /> Geri Al
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleArchive(j); }}
                          className="text-xs h-8 border-[#DCE2E8] text-slate-600 hover:text-rose-600"
                          data-testid={`job-mobile-archive-${j.job_number}`}
                        >
                          <Archive size={12} className="mr-1" /> Arşivle
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <Table className="hidden md:table">
              <TableHeader>
                <TableRow className="bg-[#F4F6F8] hover:bg-[#F4F6F8]">
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İş No</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Müşteri</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">İlçe</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Ürünler</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Satış Personeli</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">Satış Tutarı</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Durum</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Öncelik</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Montaj</TableHead>
                  <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Sözleşme Tarihi</TableHead>
                  {isAdmin && <TableHead className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 text-right">İşlem</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((j) => (
                  <TableRow
                    key={j.id}
                    className="gp-row cursor-pointer border-b border-[#DCE2E8]"
                    onClick={() => navigate(`/isler/${j.id}`)}
                    data-testid={`job-row-${j.job_number}`}
                  >
                    <TableCell className="font-mono text-sm font-semibold text-[#FF353A]">{j.job_number}</TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium text-[#171A1F]">{j.customer_name}</div>
                      {j.company_name && <div className="text-xs text-slate-500">{j.company_name}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{j.district || "-"}</TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {j.product_group_names?.length ? j.product_group_names.join(", ") : "-"}
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{j.salesperson_name || "-"}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-medium">{formatCurrency(j.sales_amount)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <StatusBadge tone={jobStatusTone(j.status)} testId={`job-status-${j.job_number}`}>
                          {JOB_STATUS_LABELS[j.status] || j.status}
                        </StatusBadge>
                        {j.is_installation_delayed && (
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-800 border border-rose-200" data-testid={`job-installation-delayed-${j.job_number}`}>
                            Montaj Gecikmiş
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-700">{PRIORITY_LABELS[j.priority] || j.priority}</TableCell>
                    <TableCell className="text-sm text-slate-700">{formatDate(j.planned_installation_start)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDate(j.contract_date)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        {j.is_archived ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleUnarchive(j); }}
                            className="text-slate-600 hover:text-slate-900"
                            data-testid={`job-unarchive-${j.job_number}`}
                          >
                            <RotateCcw size={14} className="mr-1" /> Geri Al
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleArchive(j); }}
                            className="text-slate-600 hover:text-rose-600"
                            data-testid={`job-archive-${j.job_number}`}
                          >
                            <Archive size={14} className="mr-1" /> Arşivle
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
        {!loading && items.length > 0 && (
          <Pagination
            page={parseInt(searchParams.get("page") || "1", 10)}
            pageSize={parseInt(searchParams.get("page_size") || "25", 10)}
            total={total}
            onPageChange={(p) => applyFiltersToUrl({ page: p })}
            onPageSizeChange={(s) => {
              const p = new URLSearchParams(searchParams);
              p.set("page_size", String(s));
              p.set("page", "1");
              setSearchParams(p);
            }}
          />
        )}
      </div>
    </div>
  );
}
